/**
 * The wire between the Worker drawing a sprite and the page watching it.
 *
 * Server-sent events rather than a socket, because everything here goes one
 * way: the browser asks for a sprite and then has nothing more to say until it
 * arrives. A socket would be a second protocol, a second failure mode and a
 * second thing to hold open, in exchange for a direction nobody uses.
 *
 * Both halves live in one file on purpose. A writer and a reader kept apart
 * drift, and the drift shows up as a sprite that will not draw rather than as
 * an error, because a frame that does not parse is silently nothing. Written
 * together, they can be tested against each other.
 */

/** One event, as it goes down the wire. */
export const sseFrame = (event: string, data: unknown): string =>
	`event: ${event}\ndata: ${JSON.stringify(data)}\n\n`;

/**
 * A line that means nothing except that the connection is alive.
 *
 * A draw thinks for the better part of a minute before it writes a character,
 * and a response that sends no bytes for that long is one an intermediary may
 * decide to close. This costs nine bytes and is ignored by any reader.
 */
export const SSE_ALIVE = ": still drawing\n\n";

export interface SseMessage {
	event: string;
	data: unknown;
}

/**
 * Text arriving in pieces, turned back into events.
 *
 * The same shape as the spec scanner and for the same reason: the network
 * splits a response wherever it likes, including in the middle of the word
 * `event`, so nothing may be read until the blank line that ends its frame has
 * arrived.
 */
export class SseReader {
	private buf = "";

	push(text: string): SseMessage[] {
		this.buf += text;
		const out: SseMessage[] = [];
		for (;;) {
			const end = this.buf.indexOf("\n\n");
			if (end === -1) return out;
			const frame = this.buf.slice(0, end);
			this.buf = this.buf.slice(end + 2);
			const message = read(frame);
			if (message) out.push(message);
		}
	}
}

/**
 * One frame, or null if there is nothing in it worth reporting.
 *
 * A frame with no `event:` line is a keep-alive or a comment. A frame whose
 * data will not parse is dropped rather than thrown, because a reader that
 * throws mid-draw takes the sprite with it, and the `done` event is what the
 * draw actually depends on.
 */
function read(frame: string): SseMessage | null {
	let event = "";
	const data: string[] = [];
	for (const line of frame.split("\n")) {
		if (line.startsWith("event:")) event = line.slice(6).trim();
		// Multiple data lines are one value split by newlines, which is the
		// specification's own rule. Nothing here writes them, but a proxy that
		// reflows a frame would, and joining costs one line.
		else if (line.startsWith("data:")) data.push(line.slice(5).trimStart());
	}
	if (!event) return null;
	try {
		return { event, data: JSON.parse(data.join("\n")) };
	} catch {
		return null;
	}
}
